import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { favouritesAPI } from '../utils/api';
import { Heart } from 'lucide-react';

/**
 * Heart button to save/unsave a job. Only visible to candidates.
 * Silently returns null if the user is not a candidate.
 */
export default function FavouriteButton({ jobId, className = '' }) {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const role = localStorage.getItem('trabadju_session');
    if (role !== 'candidate') { setVisible(false); return; }
    setVisible(true);
    favouritesAPI.check(jobId)
      .then((r) => setSaved(r.data.saved))
      .catch(() => setSaved(false));
  }, [jobId]);

  if (!visible) return null;

  const toggle = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (busy) return;
    setBusy(true);
    try {
      if (saved) {
        await favouritesAPI.remove(jobId);
        setSaved(false);
      } else {
        await favouritesAPI.add(jobId);
        setSaved(true);
      }
    } catch (_) {
      // swallow — no toast spam
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-label={saved ? t('favourites_btn.ariaRemove') : t('favourites_btn.ariaAdd')}
      title={saved ? t('favourites_btn.tooltipRemove') : t('favourites_btn.tooltipAdd')}
      data-testid={`favourite-btn-${jobId}`}
      className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition-all
        ${saved
          ? 'bg-red-50 border-red-200 text-red-500 hover:bg-red-100'
          : 'bg-white border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200'}
        ${busy ? 'opacity-60' : ''} ${className}`}
    >
      <Heart className={`h-5 w-5 ${saved ? 'fill-red-500' : ''}`} />
    </button>
  );
}
